import React, { useState } from 'react';
import useSWR from 'swr';
import api from '../../../services/api';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface SyncLog {
  id: number;
  emailAccountId: number;
  status: 'started' | 'completed' | 'error' | 'warning';
  message: string;
  details?: {
    emailsFound?: number;
    emailsSaved?: number;
    emailsFailed?: number;
    errors?: Array<{ messageId?: string; error: string }>;
    warnings?: string[];
  };
  emailsFound: number;
  emailsSaved: number;
  emailsFailed: number;
  duration?: number;
  errorMessage?: string;
  createdAt: string;
}

interface SyncLogsSectionProps {
  accountId: number;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-200 dark:border-green-800';
    case 'error':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border-red-200 dark:border-red-800';
    case 'warning':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800';
    case 'started':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200 dark:border-blue-800';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600';
  }
};

const SyncLogsSection: React.FC<SyncLogsSectionProps> = ({ accountId }) => {
  const { colors } = useTheme();
  const [limit, setLimit] = useState(20);
  const { data: logs, isLoading, mutate } = useSWR<SyncLog[]>(
    `/emails/accounts/${accountId}/sync-logs?limit=${limit}`,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      refreshInterval: 5000,
      dedupingInterval: 0,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'error':
        return '✗';
      case 'warning':
        return '⚠';
      case 'started':
        return '⟳';
      default:
        return '•';
    }
  };

  if (isLoading) {
    return (
      <div className={cn('rounded-lg border p-6', colors.surface, colors.border)}>
        <h3 className={cn('text-lg font-semibold mb-4', colors.text)}>Sync Logs</h3>
        <div className="text-center py-8">
          <div className={cn('inline-block animate-spin rounded-full h-8 w-8 border-2 border-t-transparent', colors.primary)} />
          <p className={cn('mt-2 text-sm', colors.textMuted)}>Loading logs...</p>
        </div>
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <div className={cn('rounded-lg border p-6', colors.surface, colors.border)}>
        <h3 className={cn('text-lg font-semibold mb-4', colors.text)}>Sync Logs</h3>
        <div className={cn('text-center py-8', colors.textMuted)}>
          <p>No sync logs available yet.</p>
          <p className="text-sm mt-2">Logs will appear here after you trigger a sync.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border p-6', colors.surface, colors.border)}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={cn('text-lg font-semibold', colors.text)}>Sync Logs</h3>
        <div className="flex items-center space-x-2">
          <label className={cn('text-sm', colors.textMuted)}>Show:</label>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className={cn('px-2 py-1 rounded text-sm border', colors.background, colors.border, colors.text)}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            onClick={() => mutate()}
            className={cn('p-1 rounded', colors.textMuted, colors.interactiveHover)}
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {logs.map((log) => (
          <div
            key={log.id}
            className={cn('border rounded-lg p-4', getStatusColor(log.status))}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <span className="text-lg font-bold">{getStatusIcon(log.status)}</span>
                <span className="font-semibold capitalize">{log.status}</span>
                <span className="text-xs opacity-75">
                  {formatDateGlobal(log.createdAt)}
                </span>
              </div>
              {log.duration && (
                <span className="text-xs opacity-75">
                  {(log.duration / 1000).toFixed(2)}s
                </span>
              )}
            </div>

            <p className="text-sm mb-2">{log.message}</p>

            {(log.emailsFound > 0 || log.emailsSaved > 0 || log.emailsFailed > 0) && (
              <div className="flex space-x-4 text-xs mt-2 pt-2 border-t border-current border-opacity-20">
                {log.emailsFound > 0 && (
                  <span>Found: <strong>{log.emailsFound}</strong></span>
                )}
                {log.emailsSaved > 0 && (
                  <span className="text-green-700 dark:text-green-400">Saved: <strong>{log.emailsSaved}</strong></span>
                )}
                {log.emailsFailed > 0 && (
                  <span className="text-red-700 dark:text-red-400">Failed: <strong>{log.emailsFailed}</strong></span>
                )}
              </div>
            )}

            {log.errorMessage && (
              <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                <p className="text-xs font-semibold mb-1">Error:</p>
                <p className={cn('text-xs font-mono p-2 rounded', colors.surfaceHover)}>
                  {log.errorMessage}
                </p>
              </div>
            )}

            {log.details?.errors && log.details.errors.length > 0 && (
              <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                <p className="text-xs font-semibold mb-1">Email Errors:</p>
                <ul className="text-xs space-y-1">
                  {log.details.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx} className={cn('font-mono p-1 rounded', colors.surfaceHover)}>
                      {err.messageId ? `${err.messageId}: ` : ''}{err.error}
                    </li>
                  ))}
                  {log.details.errors.length > 5 && (
                    <li className="text-xs opacity-75">
                      ... and {log.details.errors.length - 5} more errors
                    </li>
                  )}
                </ul>
              </div>
            )}

            {log.details?.warnings && log.details.warnings.length > 0 && (
              <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                <p className="text-xs font-semibold mb-1">Warnings:</p>
                <ul className="text-xs space-y-1">
                  {log.details.warnings.slice(0, 3).map((warning, idx) => (
                    <li key={idx} className="opacity-90">{warning}</li>
                  ))}
                  {log.details.warnings.length > 3 && (
                    <li className="text-xs opacity-75">
                      ... and {log.details.warnings.length - 3} more warnings
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SyncLogsSection;
